import cx from 'classnames';
import React from 'react';

import { Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import SimpleModalHeader from 'components/SimpleModalHeader/SimpleModalHeader';

import { getTouchDisplayName, openFileInNewWindow, showAPIErrorAlert } from 'utils/lib';
import { loadTouchPreviewData } from 'utils/touch-preview';

const useStyles = makeStyles(() => ({
  previewContainer: {
    marginTop: '20px',
    position: 'relative',
    display: 'block',
    margin: 'auto',
  },

  previewContainerLetter: {
    // 8.5 in. x 11 in. Add extra to remove scrollbars, etc.
    maxWidth: '8.75in',
    maxHeight: '11.1in',

    width: '100%',
    height: '100%',

    overflowY: 'auto',
    overflowX: 'auto',
  },
  previewContainerPostcard6x11: {
    // 11.25 in. x 6.25 in. Add extra to remove scrollbars, etc.
    maxWidth: '11.5in',
    maxHeight: '6.25in',

    width: '100%',
    height: '100%',

    overflowY: 'auto',
    overflowX: 'auto',
  },
  previewContainerPostcard6x9: {
    // 9.25 in. x 6.25 in. Add extra to remove scrollbars, etc.
    maxWidth: '9.5in',
    maxHeight: '6.25in',

    width: '100%',
    height: '100%',

    overflowY: 'auto',
    overflowX: 'auto',
  },

  previewFrame: {
    overflow: 'hidden',
    border: '2px solid #000',
    boxSizing: 'border-box',
  },

  previewFrameLetter: {
    height: 'calc(11in + 4px)', // Add 4px for border
    width: 'calc(8.5in + 4px)',
  },
  previewFramePostcard6x11: {
    height: 'calc(6.25in + 4px)', // Add 4px for border
    width: 'calc(11.25in + 4px)',
  },
  previewFramePostcard6x9: {
    height: 'calc(6.25in + 4px)', // Add 4px for border
    width: 'calc(9.25in + 4px)',
  },

  pdfPage: {
    margin: '0 auto 20px',
    width: 'fit-content',
  },

  lagTimeText: {
    fontWeight: 500,
    margin: '20px auto',
  },
}));

const PreviewPostcardLetterModal = ({ onClose, touchTrigger, initialPreviewData }) => {
  const classes = useStyles();
  const touchName = getTouchDisplayName(touchTrigger);

  const [previewData, setPreviewData] = React.useState(initialPreviewData);

  React.useEffect(() => {
    if (initialPreviewData) return;

    (async () => {
      try {
        const data = await loadTouchPreviewData(touchTrigger);
        setPreviewData(data);
      } catch (err) {
        console.error('error', err);
        showAPIErrorAlert(err);
      }
    })();
  }, [initialPreviewData, onClose, touchTrigger]);

  React.useEffect(() => {
    if (previewData?.type !== 'pdf' || !previewData.pdf) return;

    openFileInNewWindow(previewData.pdf);
    onClose();
  }, [onClose, previewData]);

  let preview;

  if (previewData?.type === 'html') {
    if (previewData.touchTypeName === 'LETTER') {
      preview = (
        <div className={cx(classes.previewContainer, classes.previewContainerLetter)}>
          <iframe
            src={previewData.data[0]}
            title="Letter Sample"
            className={cx(classes.previewFrame, classes.previewFrameLetter)}
            frameBorder="0"
            height="100%"
            width="100%"
          />
        </div>
      );
    } else if (previewData.touchTypeName === 'POSTCARD') {
      if (previewData.type === 'html') { // HTML templates
        preview = (
          <div className={cx(classes.previewContainer, touchTrigger.metadata.size === '6x11' ? classes.previewContainerPostcard6x11 : classes.previewContainerPostcard6x9)}>
            <iframe
              src={previewData.data[0]}
              title="Postcard Front Sample"
              className={cx(classes.previewFrame, touchTrigger.metadata.size === '6x11' ? classes.previewFramePostcard6x11 : classes.previewFramePostcard6x9)}
              frameBorder="0"
              height="100%"
              width="100%"
            />
            <iframe
              src={previewData.data[1]}
              title="Postcard Rear Sample"
              className={cx(classes.previewFrame, touchTrigger.metadata.size === '6x11' ? classes.previewFramePostcard6x11 : classes.previewFramePostcard6x9)}
              frameBorder="0"
              height="100%"
              width="100%"
            />
          </div>
        );
      }
    }
  }

  return (
    <Dialog
      id="preview-modal"
      open
      scroll="paper"
      keepMounted
      maxWidth="lg"
      onClose={onClose}
    >
      <SimpleModalHeader onClose={onClose} title={`${touchName} Preview`} />

      {previewData
        ? preview
        : (
          <>
            {touchTrigger.touchType.name === 'POSTCARD' && <div className={classes.lagTimeText}>We are preparing your custom postcard with all of your personalized info...this may take up to a minute!</div>}
            <LoadingIndicator />
          </>
        )}
    </Dialog>
  );
};

export default PreviewPostcardLetterModal;
